import { useState, useEffect, useRef, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

export type PaletteMode = 'commands' | 'files'

interface Item {
  id: string
  label: string
  detail?: string
  icon?: string
  action: () => void
}

interface Props {
  isOpen: boolean
  mode: PaletteMode
  onClose: () => void
  onOpenSettings?: () => void
}

export function CommandPalette({ isOpen, mode, onClose, onOpenSettings }: Props) {
  const [query, setQuery] = useState('')
  const [items, setItems] = useState<Item[]>([])
  const [sel, setSel] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const { openTab, openTabs, setActiveTab, toggleSidebar, toggleAIPanel, setTheme } = useAppStore()

  useEffect(() => {
    if (isOpen) { setQuery(''); setSel(0); setTimeout(() => inputRef.current?.focus(), 50) }
  }, [isOpen, mode])

  const commands = useCallback((): Item[] => [
    { id: 'sidebar', label: 'Toggle Sidebar', icon: '▤', action: () => { toggleSidebar(); onClose() } },
    { id: 'ai', label: 'Toggle AI Panel', icon: '🤖', action: () => { toggleAIPanel(); onClose() } },
    { id: 'dark', label: 'Theme: Dark', icon: '🌙', action: () => { setTheme('dark'); onClose() } },
    { id: 'light', label: 'Theme: Light', icon: '☀', action: () => { setTheme('light'); onClose() } },
    { id: 'settings', label: 'Open Settings', icon: '⚙', action: () => { onOpenSettings?.(); onClose() } },
    ...openTabs.map((t) => ({
      id: `tab-${t.id}`, label: t.name, detail: t.path, icon: '📄',
      action: () => { setActiveTab(t.id); onClose() },
    })),
  ], [openTabs, toggleSidebar, toggleAIPanel, setTheme, setActiveTab, onClose, onOpenSettings])

  useEffect(() => {
    if (!isOpen) return
    if (mode === 'commands') { setItems(commands()); return }
    authFetch(apiUrl('/api/files/list')).then((r) => r.json()).then((tree) => {
      const files: Item[] = []
      const collect = (nodes: any[], prefix = '') => {
        for (const n of nodes) {
          const p = prefix ? `${prefix}/${n.name}` : n.name
          if (n.type === 'file') {
            files.push({
              id: p, label: n.name, detail: p, icon: '📄',
              action: () => {
                authFetch(apiUrl(`/api/files/read?path=${encodeURIComponent(p)}`))
                  .then((r) => r.json())
                  .then((d) => { openTab({ id: p, path: `/${p}`, name: n.name, language: n.name.split('.').pop() ?? 'text', content: d.content ?? '' }); onClose() })
                  .catch(() => onClose())
              },
            })
          }
          if (n.children) collect(n.children, p)
        }
      }
      collect(tree)
      setItems(files)
    }).catch(() => setItems([]))
  }, [isOpen, mode, commands, openTab, onClose])

  const filtered = query
    ? items.filter((i) => { const q = query.toLowerCase(); return i.label.toLowerCase().includes(q) || (i.detail?.toLowerCase().includes(q) ?? false) })
    : items

  useEffect(() => { setSel(0) }, [query])

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { onClose(); return }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, filtered.length - 1)); return }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); return }
    if (e.key === 'Enter' && filtered[sel]) { filtered[sel].action(); return }
  }

  if (!isOpen) return null

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          style={inputStyle}
          placeholder={mode === 'commands' ? '> Type a command…' : 'Search files by name…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKey}
        />
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {filtered.length === 0 && <div style={emptyStyle}>No results</div>}
          {filtered.slice(0, 20).map((item, i) => (
            <div
              key={item.id}
              style={{ ...itemStyle, background: i === sel ? 'var(--bg-3)' : undefined }}
              onClick={item.action}
              onMouseEnter={() => setSel(i)}
            >
              <span style={{ fontSize: 13, width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-0)' }}>{item.label}</span>
              {item.detail && <span style={{ fontSize: 10, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{item.detail}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 999, background: 'rgba(0,0,0,0.5)',
  display: 'flex', justifyContent: 'center', paddingTop: 80,
}
const boxStyle: React.CSSProperties = {
  width: 480, maxWidth: '90vw', background: 'var(--bg-1)', border: '1px solid var(--border-2)',
  borderRadius: 12, boxShadow: '0 16px 48px rgba(0,0,0,0.5)', overflow: 'hidden',
}
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '14px 16px', background: 'none', border: 'none',
  borderBottom: '1px solid var(--border)', outline: 'none',
  color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 13,
}
const itemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
  cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12,
  transition: 'background 0.08s',
}
const emptyStyle: React.CSSProperties = {
  padding: 20, textAlign: 'center', color: 'var(--text-2)',
  fontFamily: 'var(--font-mono)', fontSize: 12,
}
